import { apiFetch } from '../api-client';

export type PayrollStatus = 'DRAFT' | 'PENDING_KTT' | 'PENDING_CEO' | 'APPROVED' | 'DISBURSED' | 'REJECTED';

export interface PayrollAllocationItem {
  account: string;
  name: string;
  pct: number;
  amount: number;
}

export interface PayrollLine {
  id: string;
  payrollId: string;
  employeeId: string;
  employeeName: string;
  baseSalary: number;
  agreedSalary: number;
  overtimeAmount: number;
  allowanceTaxFree: number;
  allowanceTaxable: number;
  bonusAmount: number;
  bhxhNv: number;
  bhytNv: number;
  bhtnNv: number;
  personalIncomeTax: number;
  advanceDeduct: number;
  advanceNote?: string | null;
  grossAmount: number;
  netAmount: number;
  workingDays: number | null;
  otHours: number | null;
}

export interface Payroll {
  id: string;
  code: string;
  period: string;
  companyKey: string;
  department: string;
  type: string;
  employeeCount: number;
  // Breakdown
  basicSalary: number;
  allowanceExempt: number;
  allowanceTaxable: number;
  bonus: number;
  gross: number;
  bhEmployee: number;
  tncn: number;
  advance: number;
  net: number;
  bhCompany: number;
  prevPeriodNet: number;
  allocationJson: PayrollAllocationItem[] | null;
  status: PayrollStatus;
  createdBy: string;
  createdAt: string;
  submittedAt: string | null;
  approvedAt: string | null;
  disbursedAt: string | null;
}

export interface PayrollsSummary {
  currentPeriod: string;
  daysUntilPeriodEnd: number;
  totalCount: number;
  disbursedCount: number;
  inProgressCount: number;
  totalPaidYtd: number;
  paidPrevYear: number;
  paidGrowthPct: number | null;
  needActionCount: number;
  pendingKttCount: number;
  pendingCeoCount: number;
}

export interface PayrollAttendancePreview {
  provider: 'erp-base-hr';
  salarySource: 'employee-profile';
  entityId: string;
  month: string;
  period: string;
  status: 'locked';
  employeeCount: number;
  fullDayCount: number;
  leaveCount: number;
  unpaidCount: number;
  totalPayrollDays: number;
  totalOtHours: number;
  unmappedEmployeeIds?: string[];
  items: Array<{ employeeId: string; employeeName: string; payrollDays: number; otHours: number; annualLeaveDays: number; unpaidDays: number; position: string; totalIncome: number; estimatedMonthlySalary: number; employmentType: 'CT' | 'TV'; attendanceMissing?: boolean; contractEndNote?: string; salaryChangeNote?: string }>;
}

export interface PayrollLegalEntity {
  id: string;
  key: string;
  name: string;
  department: string;
}

export interface PayrollListParams {
  page?: number;
  limit?: number;
  search?: string;
  year?: number;
  status?: 'processing' | 'disbursed';
  department?: string;
  type?: string;
}

export interface PayrollPaging {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
}

export const payrollsApi = {
  list: (params?: PayrollListParams) => {
    const q = new URLSearchParams();
    if (params?.page) q.set('page', String(params.page));
    if (params?.limit) q.set('limit', String(params.limit));
    if (params?.search) q.set('search', params.search);
    if (params?.year) q.set('year', String(params.year));
    if (params?.status) q.set('status', params.status);
    if (params?.department) q.set('department', params.department);
    if (params?.type) q.set('type', params.type);
    const suffix = q.toString() ? `?${q.toString()}` : '';
    return apiFetch<{ items: Payroll[]; paging: PayrollPaging }>(`/api/payrolls${suffix}`);
  },
  summary: () => apiFetch<PayrollsSummary>('/api/payrolls/summary'),
  legalEntities: () => apiFetch<{ provider: 'erp-base-hr'; items: PayrollLegalEntity[] }>('/api/payrolls/legal-entities'),
  attendancePreview: (period: string, company: string) => apiFetch<PayrollAttendancePreview>(
    `/api/payrolls/attendance-preview?period=${encodeURIComponent(period)}&company=${encodeURIComponent(company)}`,
  ),
  duplicateCheck: (period: string, company: string, type: string) => apiFetch<{ exists: boolean; payroll: Pick<Payroll, 'id' | 'code' | 'status' | 'period' | 'companyKey' | 'type'> | null }>(
    `/api/payrolls/duplicate-check?period=${encodeURIComponent(period)}&company=${encodeURIComponent(company)}&type=${encodeURIComponent(type)}`,
  ),
  findOne: (id: string) => apiFetch<Payroll>(`/api/payrolls/${id}`),
  lines: (id: string) => apiFetch<{ items: PayrollLine[] }>(`/api/payrolls/${id}/lines`),
  syncSalaryAdvances: (id: string) => apiFetch<{ items: PayrollLine[] }>(`/api/payrolls/${id}/lines/sync-salary-advances`, { method: 'POST' }),
  setLineAdvance: (payrollId: string, lineId: string, amount: number, note: string) => apiFetch<PayrollLine>(`/api/payrolls/${payrollId}/lines/${lineId}/advance`, { method: 'PATCH', body: JSON.stringify({ amount, note }) }),
  applyBulkBonus: (id: string, lineIds: string[], bonusAmount: number) => apiFetch<{ items: PayrollLine[] }>(`/api/payrolls/${id}/lines/bulk-bonus`, { method: 'POST', body: JSON.stringify({ lineIds, bonusAmount }) }),
  create: (input: Pick<Payroll, 'code' | 'period' | 'department' | 'type'> & Partial<Pick<Payroll, 'employeeCount' | 'gross' | 'net' | 'basicSalary' | 'allowanceExempt' | 'allowanceTaxable' | 'bonus' | 'bhEmployee' | 'tncn' | 'advance' | 'bhCompany'>> & { companyKey?: string; attendanceEmployeeIds?: string[] }) =>
    apiFetch<Payroll>('/api/payrolls', { method: 'POST', body: JSON.stringify(input) }),
  submit: (id: string) => apiFetch<Payroll>(`/api/payrolls/${id}/submit`, { method: 'POST' }),
  approve: (id: string) => apiFetch<Payroll>(`/api/payrolls/${id}/approve`, { method: 'POST' }),
  reject: (id: string) => apiFetch<Payroll>(`/api/payrolls/${id}/reject`, { method: 'POST' }),
  disburse: (id: string) => apiFetch<Payroll>(`/api/payrolls/${id}/disburse`, { method: 'POST' }),
  sendPayslips: (id: string) => apiFetch<{ provider: 'mock-base-email'; payrollId: string; payrollCode: string; sentAt: string; sent: Array<{ employeeId: string; employeeName: string; email: string | null; netAmount: number; status: 'queued' | 'missing_email' }> }>(`/api/payrolls/${id}/send-payslips`, { method: 'POST' }),
  remove: (id: string) => apiFetch<{ ok: true }>(`/api/payrolls/${id}`, { method: 'DELETE' }),
};
